import { useState } from "react";
import { FaChevronLeft, FaChevronRight, FaCheck } from "react-icons/fa";
import Navbar from "./Navbar";
import Ribbon_1 from "../assets/loanInMinutes/Rectangle_1.png"
import Ribbon_2 from "../assets/loanInMinutes/Rectangle_2.png"

type Testimonial = {
  rating: number;
  quote: string;
  name: string;
  role: string;
  initials: string;
  color: string;
};

// Add only real customer reviews here (with the customer's permission).
// The section stays hidden while this list is empty.
const testimonials: Testimonial[] = [];

const DESKTOP_PER_PAGE = 3;

const TestimonialCard = ({ t }: { t: Testimonial }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-6 lg:p-8 flex flex-col gap-5 h-full">
    <div className="flex items-center gap-1">
      {Array.from({ length: t.rating }).map((_, i) => (
        <span key={i} className="text-yellow-400 text-lg">★</span>
      ))}
    </div>
    <p className="text-gray-600 text-sm leading-relaxed flex-1">"{t.quote}"</p>
    <div className="flex items-center gap-4 pt-2 border-t border-gray-50">
      <span
        className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm text-[#1a1a4e] flex-shrink-0"
        style={{ backgroundColor: t.color }}
      >
        {t.initials}
      </span>
      <div className="flex flex-col">
        <span className="text-[#1a1a4e] font-bold text-sm">{t.name}</span>
        <span className="text-gray-400 text-xs">{t.role}</span>
        <span className="flex items-center gap-1 text-[#CC1A1A] text-xs font-semibold mt-0.5">
          <FaCheck className="text-[10px]" />
          Verified User
        </span>
      </div>
    </div>
  </div>
);

export const TestimonialsSection = () => {
  const [page, setPage] = useState(0);
  const [mobileIndex, setMobileIndex] = useState(0);

  const totalPages = Math.ceil(testimonials.length / DESKTOP_PER_PAGE);
  const visible = testimonials.slice(page * DESKTOP_PER_PAGE, page * DESKTOP_PER_PAGE + DESKTOP_PER_PAGE);

  const prevDesktop = () => setPage((p) => Math.max(0, p - 1));
  const nextDesktop = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  const prevMobile = () => setMobileIndex((i) => Math.max(0, i - 1));
  const nextMobile = () => setMobileIndex((i) => Math.min(testimonials.length - 1, i + 1));

  if (testimonials.length === 0) return null;

  return (
    <section className="py-20 relative overflow-hidden">
  
      <img src={Ribbon_1} className="absolute  -top-50 -right-10 sm:-top-120 opacity-100 sm:-right-5 w-70 h-200 sm:w-100 sm:h-420 rotate-[-35deg]" />
      <img src={Ribbon_2} className="absolute -left-10 top-40 sm:top-35 opacity-100 sm:-left-50 w-60 h:300 sm:w-60 sm:h-300 rotate-[-15deg]  sm:rotate-[30deg]" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-14">
          <span className="text-[#CC1A1A] text-xs font-black tracking-[0.3em] uppercase mb-3">
            TESTIMONIALS
          </span>
          <div className="w-8 h-0.5 bg-[#CC1A1A] mb-5" />
          <h2 className="text-4xl xl:text-5xl font-bold text-[#1a1a4e] mb-4">
            Real Stories. Real Results
          </h2>
          <p className="text-gray-400 font-light text-base max-w-xl">
            Here's what our customers have to say.
          </p>
        </div>

        {/* ── MOBILE: peek carousel ── */}
        <div className="lg:hidden">
          <div className="overflow-hidden -mx-6 mb-8">
            <div
              className="flex transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(calc(10vw + ${mobileIndex} * -82vw))` }}
            >
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  style={{ minWidth: "80vw", marginRight: "2vw" }}
                >
                  <TestimonialCard t={t} />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile nav */}
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={prevMobile}
                disabled={mobileIndex === 0}
                className="w-11 h-11 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="text-sm" />
              </button>
              <button
                onClick={nextMobile}
                disabled={mobileIndex === testimonials.length - 1}
                className="w-11 h-11 rounded-full bg-[#1a1a4e] flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <FaChevronRight className="text-sm" />
              </button>
            </div>

            {/* Mobile dots — one per testimonial */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setMobileIndex(i)}
                  className={`rounded-full transition-all ${
                    i === mobileIndex
                      ? "w-6 h-2.5 bg-[#1a1a4e]"
                      : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── DESKTOP: 3-column grid ── */}
        <div className="hidden lg:block">
          <div className="grid grid-cols-3 gap-6 mb-10">
            {visible.map((t) => (
              <TestimonialCard key={t.name} t={t} />
            ))}
          </div>

          {/* Desktop nav */}
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={prevDesktop}
                disabled={page === 0}
                className="w-11 h-11 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-500 hover:border-[#CC1A1A] hover:text-[#CC1A1A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <FaChevronLeft className="text-sm" />
              </button>
              <button
                onClick={nextDesktop}
                disabled={page === totalPages - 1}
                className="w-11 h-11 rounded-full border-2 border-gray-300 flex items-center justify-center text-gray-500 hover:border-[#CC1A1A] hover:text-[#CC1A1A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <FaChevronRight className="text-sm" />
              </button>
            </div>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`rounded-full transition-all ${
                    i === page
                      ? "w-6 h-2.5 bg-[#CC1A1A]"
                      : "w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <div className="pt-[73px]">
        <TestimonialsSection />
      </div>
    </div>
  );
};

export default Testimonials;
